import { DEVELOPER_GROUP_LABELS, type DeveloperGroup } from '@company/shared'
import { X, Plus, Code, TestTube, GitPullRequest } from 'lucide-react'
import { useState } from 'react'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useActiveEmployees , useProjectMembers } from '@/hooks'
import {
  useStageAssignments,
  useUpdateStageAssignments,
  type Stage,
  type StageAssignmentInput,
} from '@/hooks'
import { flattenMembers } from '@/lib/member-utils'

interface StageAssignmentPanelProps {
  issueId: string
  projectId: string
}

const STAGE_CONFIG: Record<Stage, { label: string; icon: typeof Code; color: string }> = {
  development: { label: '开发', icon: Code, color: 'text-blue-600' },
  code_review: { label: '代码审查', icon: GitPullRequest, color: 'text-purple-600' },
  testing: { label: '测试', icon: TestTube, color: 'text-orange-600' },
}

export function StageAssignmentPanel({ issueId, projectId }: StageAssignmentPanelProps) {
  const [showPicker, setShowPicker] = useState<Stage | null>(null)

  const { data: stageAssignments, isLoading } = useStageAssignments(issueId)
  const { data: groupedMembers } = useProjectMembers(projectId)
  const { data: activeEmployees } = useActiveEmployees()
  const updateStageAssignments = useUpdateStageAssignments()

  const members = flattenMembers(groupedMembers)

  // 创建员工ID到名称的映射
  const employeeNameMap = new Map<string, string>()
  activeEmployees?.forEach((emp) => {
    employeeNameMap.set(emp.id, emp.name)
  })

  const getAssigneeId = (stage: Stage): string | null => {
    if (!stageAssignments) {return null}
    switch (stage) {
      case 'development':
        return stageAssignments.developmentAssigneeId
      case 'code_review':
        return stageAssignments.codeReviewAssigneeId
      case 'testing':
        return stageAssignments.testingAssigneeId
    }
  }

  const handleSetAssignee = (stage: Stage, employeeId: string | null) => {
    const input: StageAssignmentInput = {
      developmentAssigneeId: stage === 'development' ? employeeId : stageAssignments?.developmentAssigneeId,
      codeReviewAssigneeId: stage === 'code_review' ? employeeId : stageAssignments?.codeReviewAssigneeId,
      testingAssigneeId: stage === 'testing' ? employeeId : stageAssignments?.testingAssigneeId,
    }
    updateStageAssignments.mutate({ issueId, data: input })
    setShowPicker(null)
  }

  const handleRemoveAssignee = (stage: Stage) => {
    handleSetAssignee(stage, null)
  }

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">加载中...</div>
  }

  return (
    <div className="space-y-4">
      <h4 className="font-medium">阶段负责人</h4>
      <p className="text-xs text-muted-foreground">
        配置各阶段的负责人，任务状态变更时将自动切换负责人
      </p>

      {/* 阶段列表 */}
      <div className="space-y-3">
        {(Object.keys(STAGE_CONFIG) as Stage[]).map((stage) => {
          const config = STAGE_CONFIG[stage]
          const Icon = config.icon
          const assigneeId = getAssigneeId(stage)
          const assigneeName = assigneeId ? employeeNameMap.get(assigneeId) : null

          return (
            <div key={stage} className="rounded-md border p-3">
              <div className="mb-2 flex items-center gap-2">
                <Icon className={`h-4 w-4 ${config.color}`} />
                <span className="text-sm font-medium">{config.label}</span>
              </div>

              {assigneeId && assigneeName ? (
                <div className="flex items-center justify-between rounded-md bg-muted/50 p-2">
                  <div className="flex items-center gap-2">
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-xs font-medium">
                      {assigneeName.charAt(0)}
                    </div>
                    <span className="text-sm">{assigneeName}</span>
                  </div>
                  <button
                    onClick={() => handleRemoveAssignee(stage)}
                    className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                    title="移除负责人"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowPicker(stage)}
                  className="flex w-full items-center gap-2 rounded-md border border-dashed p-2 text-sm text-muted-foreground hover:bg-muted"
                >
                  <Plus className="h-4 w-4" />
                  指定{config.label}负责人
                </button>
              )}
            </div>
          )
        })}
      </div>

      {/* 选择负责人弹窗 */}
      {showPicker && (
        <MemberPickerModal
          title={`选择${STAGE_CONFIG[showPicker].label}负责人`}
          members={members}
          employeeNameMap={employeeNameMap}
          onSelect={(employeeId) => handleSetAssignee(showPicker, employeeId)}
          onClose={() => setShowPicker(null)}
        />
      )}
    </div>
  )
}

interface MemberPickerModalProps {
  title: string
  members: Array<{
    employeeId: string
    developerGroup: DeveloperGroup | null
    isLeader: boolean
  }>
  employeeNameMap: Map<string, string>
  onSelect: (employeeId: string) => void
  onClose: () => void
}

function MemberPickerModal({
  title,
  members,
  employeeNameMap,
  onSelect,
  onClose,
}: MemberPickerModalProps) {
  const [search, setSearch] = useState('')
  const [selectedGroup, setSelectedGroup] = useState<DeveloperGroup | ''>('')

  const filteredMembers = members.filter((m) => {
    if (selectedGroup && m.developerGroup !== selectedGroup) {return false}
    const name = employeeNameMap.get(m.employeeId) || ''
    if (search && !name.toLowerCase().includes(search.toLowerCase())) {return false}
    return true
  })

  // 按开发组分组
  const groupedMembers = filteredMembers.reduce((acc, member) => {
    const group = member.developerGroup || 'other'
    if (!acc[group]) {acc[group] = []}
    acc[group].push(member)
    return acc
  }, {} as Record<string, typeof filteredMembers>)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg bg-background p-4 shadow-lg">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-medium">{title}</h3>
          <button onClick={onClose} className="rounded p-1 hover:bg-muted">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* 开发组筛选 */}
        <div className="mb-4">
          <Select
            value={selectedGroup || 'all'}
            onValueChange={(value) => setSelectedGroup(value === 'all' ? '' : value as DeveloperGroup)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="全部开发组" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">全部开发组</SelectItem>
              {(Object.keys(DEVELOPER_GROUP_LABELS) as DeveloperGroup[]).map((group) => (
                <SelectItem key={group} value={group}>
                  {DEVELOPER_GROUP_LABELS[group]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* 搜索 */}
        <div className="mb-4">
          <input
            type="text"
            placeholder="搜索成员..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
          />
        </div>

        {/* 成员列表 */}
        <div className="max-h-64 space-y-4 overflow-y-auto">
          {Object.entries(groupedMembers).map(([group, groupMembers]) => (
            <div key={group}>
              <div className="mb-2 text-sm font-medium text-muted-foreground">
                {DEVELOPER_GROUP_LABELS[group as DeveloperGroup] || '其他'} ({groupMembers.length}人)
              </div>
              <div className="space-y-1">
                {groupMembers.map((member) => {
                  const name = employeeNameMap.get(member.employeeId) || member.employeeId.slice(0, 8)
                  return (
                    <button
                      key={member.employeeId}
                      onClick={() => onSelect(member.employeeId)}
                      className="flex w-full items-center gap-2 rounded-md p-2 text-left hover:bg-muted"
                    >
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-sm font-medium">
                        {name.charAt(0)}
                      </div>
                      <div>
                        <div className="text-sm">{name}</div>
                        {member.isLeader && (
                          <div className="text-xs text-muted-foreground">⭐ 组长</div>
                        )}
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
          {filteredMembers.length === 0 && (
            <div className="py-4 text-center text-sm text-muted-foreground">
              没有可选的成员
            </div>
          )}
        </div>

        <div className="mt-4 flex justify-end">
          <button
            onClick={onClose}
            className="rounded-md px-4 py-2 text-sm font-medium hover:bg-muted"
          >
            取消
          </button>
        </div>
      </div>
    </div>
  )
}
